import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd';
import { GripVertical, Loader2, RotateCcw, Save, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/components/ui/use-toast';
import {
  listAllAdminVideos,
  setAdminVideoSortOrder,
  toAdminApiErrorMessage,
} from '@/api/adminVideos';

function idListOf(videos) {
  return videos.map((video) => video.id).join(',');
}

/**
 * カテゴリ内の並び替え。
 *
 * ドラッグ中はこの中の state だけを入れ替え、「保存」までDBには書かない。
 * 保存は新しい表示順の全件を送り、`sort_order` の割り当ては Lambda 側が行う。
 *
 * ページを跨いだ並び替えはできないため、対象カテゴリの全件を取得して1画面に並べる。
 */
export default function AdminVideoReorderPanel({ categoryId, categoryLabel, onClose }) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [orderedVideos, setOrderedVideos] = useState([]);
  const [isSeeded, setIsSeeded] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const videosQuery = useQuery({
    // `sort` を渡さない = sort_order 昇順
    queryKey: ['admin-videos-reorder', categoryId],
    queryFn: () => listAllAdminVideos({ categoryId, view: 'card' }),
    enabled: Boolean(categoryId),
    // 並び替え中に裏で取り直して、ドラッグした並びを巻き戻さないようにする
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });

  const savedVideos = videosQuery.data?.items ?? [];

  // 取得結果を編集状態の初期値にするのは1回だけ
  useEffect(() => {
    if (isSeeded || !videosQuery.data) return;
    setOrderedVideos(savedVideos);
    setIsSeeded(true);
  }, [isSeeded, videosQuery.data, savedVideos]);

  /**
   * 保存すると、カテゴリ内で今使われている sort_order が昇順で割り当て直される。
   * その割り当て先を先に見せておく（Lambda 側と同じ計算）。
   */
  const assignedSortOrders = useMemo(
    () => savedVideos.map((video) => Number(video.sort_order ?? 0) || 0).sort((a, b) => a - b),
    [savedVideos]
  );

  const hasChanges = isSeeded && idListOf(savedVideos) !== idListOf(orderedVideos);

  const saveMutation = useMutation({
    mutationFn: (videoIds) => setAdminVideoSortOrder(categoryId, videoIds),
    onSuccess: async (result) => {
      setErrorMessage(null);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['admin-videos'] }),
        queryClient.invalidateQueries({ queryKey: ['admin-videos-reorder', categoryId] }),
      ]);
      toast({
        description:
          result.updated === 0
            ? '並び順の変更はありませんでした'
            : `動画の並び順を変更しました（${result.updated}件）`,
      });
      onClose();
    },
    onError: (error) => {
      // 並べ直した内容を捨てないよう、画面はそのままにする
      const message = toAdminApiErrorMessage(error, '並び順の保存に失敗しました。');
      setErrorMessage(message);
      toast({ variant: 'destructive', description: message });
    },
  });

  const handleDragEnd = (result) => {
    if (!result.destination) return;
    if (result.destination.index === result.source.index) return;

    setErrorMessage(null);
    setOrderedVideos((current) => {
      const next = [...current];
      const [moved] = next.splice(result.source.index, 1);
      next.splice(result.destination.index, 0, moved);
      return next;
    });
  };

  const handleReset = () => {
    if (saveMutation.isPending) return;
    setErrorMessage(null);
    setOrderedVideos(savedVideos);
  };

  const handleSave = () => {
    if (saveMutation.isPending || !hasChanges) return;
    setErrorMessage(null);
    saveMutation.mutate(orderedVideos.map((video) => video.id));
  };

  return (
    <div className="rounded-2xl border border-[#e8dccd] bg-white p-4">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="font-semibold text-[#2d1810]">並び替え: {categoryLabel}</p>
          <p className="mt-1 text-xs text-[#6b5540]">
            ドラッグで順番を変えて「保存」を押してください。保存するまでDBは変わりません。
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onClose}
          disabled={saveMutation.isPending}
          className="gap-2 border-[#d4c4b0] text-[#5d4529] hover:bg-[#f5ede3]"
        >
          <X className="h-4 w-4 text-[#8b6f47]" />
          並び替えを終了
        </Button>
      </div>

      {errorMessage && (
        <div
          role="alert"
          className="mb-3 rounded-xl border border-[#d98a7a] bg-[#fdf1ee] px-4 py-3 text-sm text-[#8a3b28]"
        >
          {errorMessage}
        </div>
      )}

      {videosQuery.isLoading ? (
        <div className="space-y-2">
          {[...Array(5)].map((_, index) => (
            <Skeleton key={index} className="h-16 w-full rounded-xl bg-[#d4c4b0]" />
          ))}
        </div>
      ) : videosQuery.isError ? (
        <p className="py-10 text-center text-sm text-[#8a3b28]">
          {toAdminApiErrorMessage(videosQuery.error, '動画一覧を取得できませんでした。')}
        </p>
      ) : orderedVideos.length === 0 ? (
        <p className="py-10 text-center text-[#6b5540]">このカテゴリに動画がありません。</p>
      ) : (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="admin-video-reorder">
            {(droppableProvided) => (
              <ul
                ref={droppableProvided.innerRef}
                {...droppableProvided.droppableProps}
                className="space-y-2"
              >
                {orderedVideos.map((video, index) => {
                  const savedIndex = savedVideos.findIndex((item) => item.id === video.id);
                  const hasMoved = savedIndex !== index;

                  return (
                    <Draggable
                      key={video.id}
                      draggableId={String(video.id)}
                      index={index}
                      isDragDisabled={saveMutation.isPending}
                    >
                      {(draggableProvided, snapshot) => (
                        <li
                          ref={draggableProvided.innerRef}
                          {...draggableProvided.draggableProps}
                          className={`flex items-center gap-3 rounded-xl border p-3 ${
                            snapshot.isDragging
                              ? 'border-[#8b6f47] bg-[#f5ede3] shadow-lg'
                              : hasMoved
                                ? 'border-[#8b6f47] bg-[#faf6f1]'
                                : 'border-[#e8dccd] bg-white'
                          }`}
                        >
                          <span
                            {...draggableProvided.dragHandleProps}
                            aria-label={`${video.title || '(タイトル未設定)'} を並び替える`}
                            className="flex-shrink-0 cursor-grab text-[#8b6f47] active:cursor-grabbing"
                          >
                            <GripVertical className="h-5 w-5" />
                          </span>

                          <span className="w-8 flex-shrink-0 text-center text-sm font-semibold text-[#5d4529]">
                            {index + 1}
                          </span>

                          <div className="aspect-video w-20 flex-shrink-0 overflow-hidden rounded bg-gradient-to-br from-[#f0e3d3] to-[#d6c0a0]">
                            {video.thumbnail_url && (
                              <img
                                src={video.thumbnail_url}
                                alt=""
                                className="h-full w-full object-cover"
                              />
                            )}
                          </div>

                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-medium text-[#2d1810]">
                              {video.title || '(タイトル未設定)'}
                            </p>
                            <p className="mt-0.5 text-xs text-[#6b5540]">
                              ID: {video.id} / sort_order: {video.sort_order}
                              {assignedSortOrders[index] != null &&
                                assignedSortOrders[index] !== video.sort_order && (
                                  <span className="text-[#8b6f47]">
                                    {' '}
                                    → {assignedSortOrders[index]}
                                  </span>
                                )}
                            </p>
                          </div>

                          {hasMoved && (
                            <Badge className="flex-shrink-0 bg-[#8b6f47] text-[10px] text-[#f5ede3]">
                              移動
                            </Badge>
                          )}
                        </li>
                      )}
                    </Draggable>
                  );
                })}
                {droppableProvided.placeholder}
              </ul>
            )}
          </Droppable>
        </DragDropContext>
      )}

      <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleReset}
          disabled={saveMutation.isPending || !hasChanges}
          className="gap-2 border-[#d4c4b0] text-[#5d4529] hover:bg-[#f5ede3]"
        >
          <RotateCcw className="h-4 w-4 text-[#8b6f47]" />
          元の並びに戻す
        </Button>
        <Button
          type="button"
          size="sm"
          onClick={handleSave}
          disabled={saveMutation.isPending || !hasChanges}
          className="gap-2 bg-[#8b6f47] text-[#f5ede3] hover:bg-[#7a6040]"
        >
          {saveMutation.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Save className="h-4 w-4" />
          )}
          保存
        </Button>
      </div>
    </div>
  );
}
